import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchModal from '../../common/SearchModal/SearchModal';
import ProviderMap from '../../common/ProviderMap/ProviderMap';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import { searchService } from '../../../api';
import { useApiCall } from '../../../hooks/useApiCall';
import styles from './HomeScreen.module.css';

const HomeScreen: FunctionComponent = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [popularProcedures, setPopularProcedures] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<any[]>([]);
  const navigate = useNavigate();
  
  const { loading: loadingPopular, execute: fetchPopularProcedures } = useApiCall(searchService.getPopularProcedures);
  const { loading: loadingRecent, execute: fetchRecentSearches } = useApiCall(searchService.getRecentSearches);
  
  useEffect(() => {
    loadHomeData();
  }, []);
  
  const loadHomeData = async () => {
    // Fetch popular procedures
    const popularResult = await fetchPopularProcedures();
    if (popularResult?.success && popularResult.data) {
      setPopularProcedures(popularResult.data.map(p => p.name));
    }
    
    // Fetch recent searches
    const recentResult = await fetchRecentSearches();
    if (recentResult?.success && recentResult.data) {
      setRecentSearches(recentResult.data);
    }
  };

  const handleSearchClick = () => {
    setShowSearchModal(true);
  };

  const handleProcedureClick = (procedure: string) => {
    setSearchQuery(procedure);
    // Navigate to search results with the procedure
    navigate(`/search?q=${encodeURIComponent(procedure)}&type=procedure`);
  };

  const handleProviderClick = (provider: any) => {
    // Navigate to provider detail page using NPI
    navigate(`/provider/${provider.npi}`);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  return (
    <div className={styles.homeScreen}>
      <div className={styles.depth0Frame0}>
        <div className={styles.depth1Frame0}>
          {/* Header */}
          <div className={styles.depth2Frame0}>
            <div className={styles.depth3Frame0}>
              <b className={styles.myHealthPlan}>Mario Health</b>
            </div>
            <div className={styles.depth3Frame1} onClick={handleProfileClick}>
              <PersonIcon 
                style={{ 
                  fontSize: '20px',
                  color: '#4a739c'
                }}
              />
            </div>
          </div>

          {/* Search Bar */}
          <div className={styles.depth2Frame1}>
            <div className={styles.depth3Frame01}>
              <div className={styles.depth4Frame0}>
                <div className={styles.depth5Frame0}>
                  <SearchIcon 
                    style={{ 
                      fontSize: '20px',
                      color: '#4a739c'
                    }}
                  />
                </div>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search for a procedure or provider"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSearchModal(true)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>

          {/* Popular Procedures */}
          <div className={styles.depth2Frame2}>
            <b className={styles.popularProcedures}>Popular Procedures</b>
          </div>
          <div className={styles.depth2Frame3}>
            {loadingPopular ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress size={24} />
              </div>
            ) : popularProcedures.length > 0 ? (
              popularProcedures.map((procedure, index) => (
                <div 
                  key={index} 
                  className={styles.depth3Frame02}
                  onClick={() => handleProcedureClick(procedure)}
                >
                  <div className={styles.depth4Frame01}>
                    <div className={styles.procedureText}>{procedure}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                No popular procedures available
              </div>
            )}
          </div>

          {/* Recent Searches */}
          <div className={styles.depth2Frame2}>
            <b className={styles.popularProcedures}>Based on your Recent Searches</b>
          </div>
          <div className={styles.depth2Frame10}>
            {loadingRecent ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <CircularProgress />
              </div>
            ) : recentSearches.length > 0 ? (
              recentSearches.map((search, index) => (
                <div 
                  key={search.id || index} 
                  className={styles.depth3Frame03}
                  onClick={() => handleProviderClick(search)}
                >
                  <img className={styles.depth3Frame04} alt={search.name} src={search.image} />
                  <div className={styles.depth3Frame12}>
                    <div className={styles.depth4Frame07}>
                      <div className={styles.drMichaelChen}>{search.name}</div>
                    </div>
                    <div className={styles.depth4Frame1}>
                      <div className={styles.miles}>{search.distance}</div>
                    </div>
                  </div>
                  {search.procedure && (
                    <div className={styles.depth3Frame21}>
                      <div className={styles.depth4Frame08}>
                        <div className={styles.depth5Frame01}>
                          <div className={styles.depth6Frame0}>
                            <div className={styles.miles}>{search.procedure}</div>
                          </div>
                        </div>
                        <div className={styles.depth5Frame11}>
                          <div className={styles.depth6Frame0}>
                            <div className={styles.div}>{search.price}</div>
                          </div>
                        </div>
                      </div>
                      {search.isChecked && (
                        <div className={styles.checkboxes}>
                          <div className={styles.stateLayer}>
                            <div className={styles.container} />
                            <div className={styles.checkSmallIcon}>
                              <CheckIcon 
                                style={{ 
                                  fontSize: '16px',
                                  color: 'white'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No recent searches yet. Start searching for providers or procedures!
              </div>
            )}
          </div>

          {/* Nearby Providers */}
          <div className={styles.depth2Frame2}>
            <b className={styles.popularProcedures}>Nearby Providers</b>
          </div>
          <div className={styles.depth2Frame7}>
            <ProviderMap height="300px" />
          </div>
        </div>
      </div>
      
      <SearchModal 
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        initialQuery={searchQuery}
      />
    </div>
  );
};

export default HomeScreen;